import { BaseAnalyticsProvider } from '../../core/base-analytics.provider';
import { PostHogConfig } from './posthog.config';
import { environment } from '../../../../environments/environment';

declare const posthog: any;

export class PostHogProvider extends BaseAnalyticsProvider {
  readonly name = 'PostHog';
  readonly providerType = 'posthog';
  readonly version = '1.0.0';
  
  private posthogInstance: any = null;

  constructor(config: PostHogConfig) {
    super(config);
  }

  async initialize(): Promise<void> {
    try {
      if (!this._config.key) {
        throw new Error('PostHog API key is required');
      }

      if (!this._config.host) {
        throw new Error('PostHog host is required');
      }

      // Initialize PostHog
      posthog.init(this._config.key, {
        api_host: this._config.host,
        disable_compression: this._config.disableCompression,
        capture_pageview: this._config.capturePageview,
        autocapture: this._config.autocapture,
        person_profiles: this._config.personProfiles,
        persistence: this._config.persistence,
        _capture_metrics: this._config.captureMetrics,
        loaded: (ph: any) => {
          if (environment.production) {
            ph.debug(false);
          } else {
            ph.debug(this._config.debugLogs || false);
          }
        }
      });

      this.posthogInstance = posthog;
      this.setInitialized(true);
      this.setLastError(null);
      
      this.logInfo('PostHog provider initialized successfully');
    } catch (error) {
      this.setLastError(error instanceof Error ? error : new Error(String(error)));
      this.setInitialized(false);
      throw error;
    }
  }

  async destroy(): Promise<void> {
    try {
      if (this.posthogInstance) {
        // PostHog doesn't have a destroy method, but we can clear the instance
        this.posthogInstance = null;
      }
      this.setInitialized(false);
      this.logInfo('PostHog provider destroyed');
    } catch (error) {
      this.logError('Error destroying PostHog provider', error);
    }
  }

  async trackEvent(eventName: string, properties?: Record<string, any>): Promise<void> {
    if (!this.isEnabled || !this.isInitialized) {
      this.logWarning('PostHog provider not enabled or not initialized');
      return;
    }

    try {
      const eventProperties = {
        ...properties,
        timestamp: new Date().toISOString(),
        source: 'posthog_provider'
      };

      this.posthogInstance.capture(eventName, eventProperties);
      this.logInfo(`Event tracked: ${eventName}`, eventProperties);
    } catch (error) {
      this.logError(`Error tracking event: ${eventName}`, error);
      throw error;
    }
  }

  async identifyUser(userId: string, properties?: Record<string, any>): Promise<void> {
    if (!this.isEnabled || !this.isInitialized) {
      this.logWarning('PostHog provider not enabled or not initialized');
      return;
    }

    try {
      this.posthogInstance.identify(userId, properties);
      this.logInfo(`User identified: ${userId}`, properties);
    } catch (error) {
      this.logError(`Error identifying user: ${userId}`, error);
      throw error;
    }
  }

  async setUserProperties(properties: Record<string, any>): Promise<void> {
    if (!this.isEnabled || !this.isInitialized) {
      this.logWarning('PostHog provider not enabled or not initialized');
      return;
    }

    try {
      this.posthogInstance.people.set(properties);
      this.logInfo('User properties set', properties);
    } catch (error) {
      this.logError('Error setting user properties', error);
      throw error;
    }
  }

  async trackPageView(properties?: Record<string, any>): Promise<void> {
    if (!this.isEnabled || !this.isInitialized) {
      this.logWarning('PostHog provider not enabled or not initialized');
      return;
    }

    try {
      const pageProperties = {
        ...properties,
        timestamp: new Date().toISOString(),
        source: 'posthog_provider'
      };

      this.posthogInstance.capture('$pageview', pageProperties);
      this.logInfo('Page view tracked', pageProperties);
    } catch (error) {
      this.logError('Error tracking page view', error);
      throw error;
    }
  }

  validateConfig(config: any): boolean {
    return config && 
           typeof config === 'object' && 
           typeof config.key === 'string' && 
           config.key.length > 0 &&
           typeof config.host === 'string' && 
           config.host.length > 0;
  }

  getProviderInfo(): Record<string, any> {
    return {
      ...super.getProviderInfo(),
      posthogInstance: !!this.posthogInstance,
      config: {
        key: this._config.key ? `${this._config.key.substring(0, 8)}...` : 'not set',
        host: this._config.host,
        debugLogs: this._config.debugLogs
      }
    };
  }
} 